import { Build } from '@todesktop/shared';
import EventEmitter from 'events';
import type { DocSnapshot } from '../../../types';
import { doc, onSnapshot } from 'firebase/firestore';
import firestore from '../../../utilities/firestore';
import { readDocumentSnapshot } from '../../../utilities/firestoreData';

export default function spyBuild() {
  const emitter = new EventEmitter();

  let unsubscribeSnapshot: (() => void) | undefined;
  let latestBuildDoc: Build | undefined;

  function when(predicate: (build: Build) => boolean) {
    if (latestBuildDoc && predicate(latestBuildDoc)) {
      return Promise.resolve(latestBuildDoc);
    }

    return new Promise<Build>((resolve, reject) => {
      emitter
        .on('update', ({ build }) => {
          if (predicate(build)) {
            resolve(build);
          }
        })
        .once('error', (e) => reject(e));
    });
  }

  return {
    onUpdate(
      callback: (data: {
        build: Build | undefined;
        snapshot: DocSnapshot<Build>;
      }) => void,
    ) {
      emitter.on('update', callback);
    },

    subscribe({
      appId,
      buildId,
      userId,
    }: {
      appId: string;
      buildId: string;
      userId: string;
    }) {
      const buildPath = `users/${userId}/applications/${appId}/builds/${buildId}`;
      unsubscribeSnapshot = onSnapshot(
        doc(firestore, buildPath),
        (snapshot) => {
          latestBuildDoc = readDocumentSnapshot<Build>(snapshot);
          emitter.emit('update', {
            build: latestBuildDoc,
            snapshot: snapshot as unknown as DocSnapshot<Build>,
          });
        },
        (err) => {
          emitter.emit('error', err);
        },
      );
    },

    unsubscribe() {
      unsubscribeSnapshot?.();
    },

    async whenFirstUpdate() {
      if (latestBuildDoc) {
        return Promise.resolve(latestBuildDoc);
      }

      return new Promise<Build>((resolve, reject) => {
        emitter
          .once('update', (data) => resolve(data))
          .once('error', (e) => reject(e));
      });
    },

    async whenSettled() {
      return when((build) => buildHasSettled(build));
    },
  };
}

function buildHasSettled(build: Build) {
  return (['linux', 'mac', 'windows'] as const).every(
    (platform) =>
      build[platform]?.shouldSkip ||
      'succeeded' === build[platform]?.status ||
      'cancelled' === build[platform]?.status ||
      ('failed' === build[platform]?.status &&
        build[platform]?.numberOfAttemptedBuilds === 2),
  );
}
